import type { Vec2 } from '../utils/vec2.js';

// Three-state fog per docs/02_GAME_DESIGN.md §5:
//   0 = unexplored — solid black; map shape only.
//   1 = explored   — last-seen grayscale; static buildings/relays show last-known
//                    state at the renderer; squads in this state are NOT drawn.
//   2 = visible    — current vision range of a friendly entity.
// Per-tick, FogSystem decays every 'visible' cell to 'explored' before re-marking
// cells covered by friendly vision sources. 'unexplored' never reverts; once seen
// it stays at least 'explored'.
export const FOG_UNEXPLORED = 0 as const;
export const FOG_EXPLORED = 1 as const;
export const FOG_VISIBLE = 2 as const;

export type FogCellState = typeof FOG_UNEXPLORED | typeof FOG_EXPLORED | typeof FOG_VISIBLE;

// Plain data per Rule 4: round-trips through JSON.stringify cleanly. We use a
// number[] (not Uint8Array) so JSON deep-equal holds; storage cost is acceptable
// at our world size (63x63 = 3969 cells per player).
export interface FogGrid {
  cellsX: number;
  cellsY: number;
  tilePx: number;
  cells: number[];
}

export interface FogGridDims {
  width: number;
  height: number;
  tilePx: number;
}

export function createFogGrid(dims: FogGridDims): FogGrid {
  const cellsX = Math.ceil(dims.width / dims.tilePx);
  const cellsY = Math.ceil(dims.height / dims.tilePx);
  return {
    cellsX,
    cellsY,
    tilePx: dims.tilePx,
    cells: new Array<number>(cellsX * cellsY).fill(FOG_UNEXPLORED),
  };
}

export function worldToCell(grid: FogGrid, p: Vec2): { cx: number; cy: number } {
  return { cx: Math.floor(p.x / grid.tilePx), cy: Math.floor(p.y / grid.tilePx) };
}

export function cellIndex(grid: FogGrid, cx: number, cy: number): number {
  return cy * grid.cellsX + cx;
}

export function getCell(grid: FogGrid, cx: number, cy: number): FogCellState {
  if (cx < 0 || cy < 0 || cx >= grid.cellsX || cy >= grid.cellsY) return FOG_UNEXPLORED;
  const v = grid.cells[cellIndex(grid, cx, cy)] ?? FOG_UNEXPLORED;
  if (v === FOG_VISIBLE) return FOG_VISIBLE;
  if (v === FOG_EXPLORED) return FOG_EXPLORED;
  return FOG_UNEXPLORED;
}

// Demote every visible cell to explored. Called at the start of each fog update
// pass; vision sources then re-mark cells they currently cover as visible.
export function demoteVisibleToExplored(grid: FogGrid): void {
  const cells = grid.cells;
  for (let i = 0; i < cells.length; i++) {
    if (cells[i] === FOG_VISIBLE) cells[i] = FOG_EXPLORED;
  }
}

// Stamp a vision circle at (px, py) with radius `radiusTiles` measured in tiles.
// Cells inside the circle become FOG_VISIBLE. Outside-the-grid cells are skipped
// silently — vision near edges simply truncates.
export function stampVision(grid: FogGrid, px: number, py: number, radiusTiles: number): void {
  if (radiusTiles <= 0) return;
  const center = worldToCell(grid, { x: px, y: py });
  const r = Math.ceil(radiusTiles);
  const r2 = radiusTiles * radiusTiles;
  const minX = Math.max(0, center.cx - r);
  const maxX = Math.min(grid.cellsX - 1, center.cx + r);
  const minY = Math.max(0, center.cy - r);
  const maxY = Math.min(grid.cellsY - 1, center.cy + r);
  for (let cy = minY; cy <= maxY; cy++) {
    const dy = cy - center.cy;
    const dy2 = dy * dy;
    for (let cx = minX; cx <= maxX; cx++) {
      const dx = cx - center.cx;
      if (dx * dx + dy2 <= r2) {
        grid.cells[cy * grid.cellsX + cx] = FOG_VISIBLE;
      }
    }
  }
}

// Returns the cell state at a world position. Convenience for the renderer and
// the minimap; handles out-of-bounds as unexplored.
export function fogAt(grid: FogGrid, p: Vec2): FogCellState {
  const c = worldToCell(grid, p);
  return getCell(grid, c.cx, c.cy);
}
